import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../lib/apiTransport';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Search, UserPlus, X, Check } from 'lucide-react';
import { resolveAvatarUrl } from '../lib/avatarUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ManageMembersSheet - manager UI to invite players and manage club members.
 *
 * Uses club-specific backend routes:
 *   - GET    /api/clubs/{clubId}/invite-candidates?q=
 *   - POST   /api/clubs/{clubId}/invites   body { invited_user_id }
 *   - DELETE /api/clubs/{clubId}/members/{userId}
 *
 * NOTE: club members[] expose the USER id as `member.id`, so removal uses `member.id`
 * as the {user_id} path param. The slug response caps the roster according to
 * the viewer's club authority. Its total count distinguishes a complete list
 * from a capped response; this sheet does not fetch additional members.
 */
export const ManageMembersSheet = ({
  open,
  onOpenChange,
  clubId,
  clubName,
  token,
  members = [],
  totalMemberCount,
  currentUserId,
  ownerUserId,
  canManageRoles = false,
  onChanged,
}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [invitingId, setInvitingId] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [roleChangingId, setRoleChangingId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const debounceRef = useRef(null);

  const memberIds = new Set((members || []).map((m) => m.id));
  const displayedMemberCount = (members || []).length;
  const hasValidTotal = Number.isInteger(totalMemberCount) && totalMemberCount >= displayedMemberCount;
  const memberCountLabel = hasValidTotal && totalMemberCount > displayedMemberCount
    ? `Showing ${displayedMemberCount} of ${totalMemberCount} members`
    : `Showing ${displayedMemberCount} members`;
  const invitedIds = new Set((pendingInvites || []).map((invite) => invite.invited_user_id));

  const memberRoleLabel = (member) => {
    const role = String(member?.role || 'member').toLowerCase();
    if (member?.id === ownerUserId || role === 'host' || role === 'owner') return 'Owner';
    if (role === 'manager' || role === 'coach') return 'Manager';
    return 'Member';
  };

  const memberRoleClass = (member) => {
    const label = memberRoleLabel(member);
    if (label === 'Owner') return 'bg-orange-500 text-white';
    if (label === 'Manager') return 'bg-blue-100 text-blue-700';
    return 'bg-zinc-100 text-zinc-600';
  };

  const runSearch = useCallback(
    (q) => {
      if (!clubId || !token || !q || q.trim().length < 2) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      axios
        .get(`${BACKEND_URL}/api/clubs/${clubId}/invite-candidates?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setResults(Array.isArray(res.data?.candidates) ? res.data.candidates : []);
        })
        .catch(() => {
          setResults([]);
        })
        .finally(() => setSearching(false));
    },
    [clubId, token]
  );

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const fetchPendingInvites = useCallback(async () => {
    if (!clubId || !token || !open) return;
    setLoadingInvites(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/clubs/${clubId}/invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingInvites(Array.isArray(res.data?.invites) ? res.data.invites : []);
    } catch (err) {
      setPendingInvites([]);
    } finally {
      setLoadingInvites(false);
    }
  }, [clubId, token, open]);

  useEffect(() => {
    if (open) {
      fetchPendingInvites();
    }
  }, [open, fetchPendingInvites]);

  const handleInvite = async (userId) => {
    if (!clubId || !userId) return;
    setInvitingId(userId);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/invites`,
        { invited_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.already_pending) {
        toast.info('Invite already pending');
      } else {
        toast.success('Invite sent');
      }
      await fetchPendingInvites();
    } catch (err) {
      // Surface backend detail: ineligible/unknown-age (403), disabled (400),
      // blocked (403), unauthorized manager (403), user-not-found (404).
      toast.error(err.response?.data?.detail || 'Could not invite member');
    } finally {
      setInvitingId(null);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!clubId || !inviteId) return;
    setRevokingId(inviteId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/invites/${inviteId}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invite revoked');
      await fetchPendingInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not revoke invite');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRemove = async (userId) => {
    if (!clubId || !userId) return;
    setRemovingId(userId);
    try {
      await axios.delete(`${BACKEND_URL}/api/clubs/${clubId}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Member removed');
      if (onChanged) await onChanged();
    } catch (err) {
      // Surface backend detail: non-member (404), host self-remove (400),
      // non-host (403).
      toast.error(err.response?.data?.detail || 'Could not remove member');
    } finally {
      setRemovingId(null);
      setConfirmRemoveId(null);
    }
  };

  const handlePromote = async (userId) => {
    if (!clubId || !userId) return;
    setRoleChangingId(userId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/members/${userId}/promote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Member promoted to Manager');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not promote member');
    } finally {
      setRoleChangingId(null);
    }
  };

  const handleDemote = async (userId) => {
    if (!clubId || !userId) return;
    setRoleChangingId(userId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clubs/${clubId}/members/${userId}/demote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Manager demoted to Member');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not demote manager');
    } finally {
      setRoleChangingId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="manage-members-sheet"
      >
        <SheetHeader>
          <SheetTitle>Manage Members</SheetTitle>
          <SheetDescription>
            Invite players or manage club members{clubName ? ` of ${clubName}` : ''}.
          </SheetDescription>
        </SheetHeader>

        {/* Search + add */}
        <div className="mt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={query}
              onChange={handleQueryChange}
              placeholder="Search by username"
              className="pl-9"
              data-testid="member-search-input"
            />
          </div>

          {searching && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 mt-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}

          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-sm text-zinc-500 mt-3" data-testid="member-search-empty">
              No eligible players found for "{query.trim()}".
            </p>
          )}

          {results.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="member-search-results">
              {results.map((u) => {
                const already = memberIds.has(u.id);
                const invited = invitedIds.has(u.id);
                return (
                  <li
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-zinc-200"
                    data-testid={`search-result-${u.id}`}
                  >
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarImage src={resolveAvatarUrl(u.profile_image_url)} />
                      <AvatarFallback className="bg-zinc-100 text-zinc-600 font-semibold">
                        {(u.username || '?').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">
                        {u.display_name || u.username}
                      </p>
                      {u.username && u.display_name !== u.username && (
                        <p className="text-xs text-zinc-500 truncate">@{u.username}</p>
                      )}
                      {u.source && (
                        <p className="text-xs text-zinc-500 truncate">
                          {u.source}
                        </p>
                      )}
                    </div>
                    {already ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                        <Check className="w-4 h-4" /> Added
                      </span>
                    ) : invited ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                        Pending
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleInvite(u.id)}
                        disabled={invitingId === u.id}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 active:scale-[0.98]"
                        data-testid={`invite-member-btn-${u.id}`}
                      >
                        {invitingId === u.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4" /> Invite
                          </>
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pending invites */}
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">
            Pending invites
          </h3>
          {loadingInvites ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading invites...
            </div>
          ) : pendingInvites.length === 0 ? (
            <p className="text-sm text-zinc-500">No pending invites.</p>
          ) : (
            <ul className="space-y-2" data-testid="pending-invite-list">
              {pendingInvites.map((invite) => {
                const invitedUser = invite.user || {};
                const displayName = invitedUser.display_name || invitedUser.username || 'Invited user';
                return (
                  <li
                    key={invite.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-zinc-200"
                    data-testid={`pending-invite-row-${invite.id}`}
                  >
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarImage src={resolveAvatarUrl(invitedUser.profile_image_url)} />
                      <AvatarFallback className="bg-amber-100 text-amber-700 font-semibold">
                        {displayName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-zinc-900 truncate">
                          {displayName}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Pending
                        </span>
                      </div>
                      {invitedUser.username && invitedUser.display_name !== invitedUser.username && (
                        <p className="text-xs text-zinc-500 mt-0.5">@{invitedUser.username}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevokeInvite(invite.id)}
                      disabled={revokingId === invite.id}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                      data-testid={`revoke-invite-btn-${invite.id}`}
                      aria-label={`Revoke invite for ${displayName}`}
                    >
                      {revokingId === invite.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Current members */}
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">
            Current members
          </h3>
          <ul className="space-y-2" data-testid="manage-member-list">
            {(members || []).map((m) => {
              const roleLabel = memberRoleLabel(m);
              const isOwnerRow = roleLabel === 'Owner';
              const isManagerRow = roleLabel === 'Manager';
              const canRemoveMember = !isOwnerRow && (!isManagerRow || canManageRoles);
              return (
                <li
                  key={m.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-zinc-200"
                  data-testid={`manage-member-row-${m.id}`}
                >
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarImage src={resolveAvatarUrl(m.profile_image_url)} />
                    <AvatarFallback className="bg-orange-100 text-orange-600 font-semibold">
                      {(m.display_name || m.username || '?').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-zinc-900 truncate">
                        {m.display_name || m.username}
                      </span>
                      <span
                        className={
                          'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ' +
                          memberRoleClass(m)
                        }
                      >
                        {roleLabel}
                      </span>
                    </div>
                  </div>

                  {canManageRoles && !isOwnerRow && (
                    <button
                      type="button"
                      onClick={() => (isManagerRow ? handleDemote(m.id) : handlePromote(m.id))}
                      disabled={roleChangingId === m.id}
                      className="h-8 px-3 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xs font-semibold transition-colors disabled:opacity-60"
                      data-testid={isManagerRow ? `demote-member-btn-${m.id}` : `promote-member-btn-${m.id}`}
                    >
                      {roleChangingId === m.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isManagerRow ? (
                        'Demote'
                      ) : (
                        'Make Manager'
                      )}
                    </button>
                  )}

                  {/* Owner row never gets a remove button. Managers can only remove non-manager members. */}
                  {canRemoveMember &&
                    (confirmRemoveId === m.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveId(null)}
                          disabled={removingId === m.id}
                          className="h-8 px-2 text-xs font-medium text-zinc-500 hover:text-zinc-700"
                          data-testid={`remove-member-cancel-${m.id}`}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(m.id)}
                          disabled={removingId === m.id}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                          data-testid={`remove-member-confirm-${m.id}`}
                        >
                          {removingId === m.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Remove'
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveId(m.id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`remove-member-btn-${m.id}`}
                        aria-label={`Remove ${m.display_name || m.username}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-zinc-400 mt-3">
            Invited users become members only after accepting. {memberCountLabel}.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ManageMembersSheet;
